import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import {
  CCard,
  CCardBody,
  CCol,
  CDataTable,
  CRow,
} from '@coreui/react'
import { APIS, METAMUI_ADMINS } from '../../config'
import { useSelector } from 'react-redux'
import { did, utils } from 'mui-metablockchain-sdk'


// Constants
const DECIMALS = 1e6
const ITEMS_PER_PAGE = 10
const PAGINATION_GROUP_SIZE = 5

const Users = (props) => {
  const history = useHistory()
  const [activePage, setActivePage] = useState(1)
  const [paginationGroup, setPaginationGroup] = useState(0)
  const [transactionsList, setTransactionsList] = useState([])
  const userDidFromRedux = useSelector(state => state.count)
  const [searchPageNumber, setSearchPageNumber] = useState(null)

  // Helper function to decode token similar to TheHeader.js
  const decodeV1Token = (token) => {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch (error) {
      console.error('Error decoding v1 token:', error);
      return null;
    }
  }

  const decodeV2Token = (token) => {
    try {
      return JSON.parse(atob(token.split(":")[1]));
    } catch (error) {
      console.error('Error decoding v2 token:', error);
      return null;
    }
  }

  // Helper function to check if user is admin (similar to TheHeader.js logic)
  const isUserAdmin = () => {
    const authToken = sessionStorage.getItem('login');
    const tokenType = sessionStorage.getItem('tokenType');
    
    if (!authToken || !tokenType) return false;

    try {
      if (tokenType === 'v1') {
        const decodedToken = decodeV1Token(authToken);
        if (!decodedToken) return false;
        return METAMUI_ADMINS.includes(decodedToken.data.did);
      } else if (tokenType === 'v2') {
        const decodedToken = decodeV2Token(authToken);
        if (!decodedToken) return false;
        
        const userDid = utils.hexToString(decodedToken.user_did);
        const userGroups = decodedToken.user_role.split(",");
        const allowedGroups = [
          "council_members",
          "kyc_admins", 
          "kyc-admins",
          "ssid-admin",
        ];

        // Check if user is in METAMUI_ADMINS list
        if (METAMUI_ADMINS.includes(userDid)) {
          return true;
        }

        // Check if user has admin groups
        return userGroups.some(group => allowedGroups.includes(group));
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
    
    return false;
  }

  useEffect(() => {
    fetchTransactionData()
    console.log('Active Page:', activePage, 'Pagination Group:', paginationGroup)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.ssid, props.selectedToken, activePage])

  // Helper function to create authenticated headers
  const getAuthHeaders = (authToken) => {
    return authToken ? { 'Authorization': authToken } : {}
  }

  // Helper function to fetch data with optional authentication
  const fetchWithAuth = async (url, authToken) => {
    const headers = getAuthHeaders(authToken)
    const response = await fetch(url, { headers })
    return response.json()
  }

  // Helper function to transform transaction data
  const transformTransactionData = (transactionData, authToken, userDid) => {
    const { sender, destination, value } = transactionData.attributes
    const isAuthenticated = authToken !== null
    const isAdmin = isUserAdmin()
    const isUserInvolved = isAuthenticated && userDid &&
      (sender.attributes.id === userDid.slice(2) || destination.attributes.id === userDid.slice(2))
    
    // Admin should see all data (hexToString format), or user should see data if they're involved in the transaction
    // Otherwise, show address format for privacy
    const shouldShowDetailedData = isAdmin || isUserInvolved

    return {
      id: transactionData.id,
      from: shouldShowDetailedData ? utils.hexToString(sender.attributes.id) : sender.attributes.address,
      to: shouldShowDetailedData ? utils.hexToString(destination.attributes.id) : destination.attributes.address,
      amt: value / DECIMALS
    }
  }

  // Helper function to process and set transaction data
  const processTransactionData = (responseData, authToken, userDid) => {
    if (responseData.data.length === 0) {
      setTransactionsList([])
      return
    }

    const transformedTransactions = responseData.data.map(transaction => 
      transformTransactionData(transaction, authToken, userDid)
    )
    
    console.log('Processed transactions:', transformedTransactions)
    setTransactionsList(transformedTransactions)
  }

  const fetchTransactionData = async () => {
    const authToken = sessionStorage.getItem('login')
    
    try {
      if (props.ssid === undefined) {
        // Fetch user's own transaction history
        if (!userDidFromRedux && !sessionStorage.getItem("userDid")) {
          console.error('User DID not found in Redux or session storage')
          setTransactionsList([])
          return
        }
        const sanitizedDid = did.sanitiseDid(userDidFromRedux || sessionStorage.getItem("userDid"))
        console.log('Sanitized DID:', sanitizedDid)
        
        const url = `${APIS}balances/transfer_history/${sanitizedDid}?page[size]=${ITEMS_PER_PAGE}&page[number]=${activePage}`
        const response = await fetchWithAuth(url, authToken)
        console.log('API response:', response)
        
        processTransactionData(response, authToken, sanitizedDid)
        
      } else if (props.selectedToken.name !== 'MetaMUI') {
        // Fetch token-specific transaction history
        const url = `${APIS}balances/token_history/?filter[did]=${props.ssid}&filter[currency]=${props.selectedToken.currencyCode}`
        
        try {
          const response = await fetchWithAuth(url, authToken)
          console.log('Selected token history:', response)
          
          let userDid = null;
          if (userDidFromRedux || sessionStorage.getItem("userDid")) {
            userDid = did.sanitiseDid(userDidFromRedux || sessionStorage.getItem("userDid"));
          }
          processTransactionData(response, authToken, userDid)
          
        } catch (error) {
          console.log('Selected token history is not available')
          setTransactionsList([])
        }
        
      } else {
        // Fetch MetaMUI transaction history for specific user
        const sanitizedDid = did.sanitiseDid(props.ssid)
        const url = `${APIS}balances/transfer_history/${sanitizedDid}?page[size]=${ITEMS_PER_PAGE}&page[number]=${activePage}`
        
        const response = await fetchWithAuth(url, authToken)
        console.log('MetaMUI transaction response:', response)

        let userDid = null;
        if (userDidFromRedux || sessionStorage.getItem("userDid")) {
          userDid = did.sanitiseDid(userDidFromRedux || sessionStorage.getItem("userDid"));
        }
        processTransactionData(response, authToken, userDid)
      }
    } catch (error) {
      console.error('Error fetching transaction data:', error)
      setTransactionsList([])
    }
  }

  // Helper function to determine if pagination should be shown
  const shouldShowPagination = () => {
    return props.ssid === undefined || (props.ssid && props.selectedToken.name === 'MetaMUI')
  }

  // Helper function to handle page navigation
  const handlePageNavigation = (newPage) => {
    setActivePage(newPage)
    if (newPage % PAGINATION_GROUP_SIZE === 1) {
      setPaginationGroup(paginationGroup - 1)
    }
    setTransactionsList([])
  }

  // Helper function to handle next page navigation
  const handleNextPage = () => {
    const newPage = activePage + 1
    setActivePage(newPage)
    if (activePage % PAGINATION_GROUP_SIZE === 0) {
      setPaginationGroup(paginationGroup + 1)
    }
  }

  // Helper function to handle previous page navigation
  const handlePreviousPage = () => {
    const newPage = activePage - 1
    setActivePage(newPage)
    if (activePage % PAGINATION_GROUP_SIZE === 1) {
      setPaginationGroup(paginationGroup - 1)
    }
  }

  // Helper function to handle search page navigation
  const handleSearchPageNavigation = () => {
    const pageNumber = parseInt(searchPageNumber)
    setActivePage(pageNumber)
    setPaginationGroup(
      pageNumber % PAGINATION_GROUP_SIZE === 0 
        ? pageNumber / PAGINATION_GROUP_SIZE - 1 
        : Math.floor(pageNumber / PAGINATION_GROUP_SIZE)
    )
    setTransactionsList([])
  }

  // Helper function to render pagination button
  const renderPaginationButton = (pageNumber) => {
    const isDisabled = (transactionsList && transactionsList.length < ITEMS_PER_PAGE) && pageNumber > activePage
    const isActive = activePage === pageNumber

    return (
      <button
        key={pageNumber}
        type="button"
        className={`btn btn-outline-dark ${isActive ? 'active' : ''}`}
        onClick={() => handlePageNavigation(pageNumber)}
        disabled={isDisabled}
      >
        {pageNumber}
      </button>
    )
  }

  // Helper function to render pagination controls
  const renderPaginationControls = () => {
    const startPage = PAGINATION_GROUP_SIZE * paginationGroup + 1
    const pageButtons = []
    
    for (let i = 0; i < PAGINATION_GROUP_SIZE; i++) {
      pageButtons.push(renderPaginationButton(startPage + i))
    }

    return (
      <div className="container">
        <div className="row justify-content-end">
          <div className="col-5">
            <div className="btn-toolbar" role="toolbar" aria-label="Pagination">
              <div className="btn-group" role="group" aria-label="Pagination">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={handlePreviousPage}
                  disabled={activePage === 1}
                >
                  <span aria-hidden="true">&laquo;</span>
                </button>
                {pageButtons}
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={handleNextPage}
                  disabled={transactionsList && transactionsList.length < ITEMS_PER_PAGE}
                >
                  <span aria-hidden="true">&raquo;</span>
                </button>
              </div>
            </div>
          </div>
          <div className="col-3">
            <div className="btn-group" role="group" aria-label="Pagination">
              <input 
                type="number" 
                min="1" 
                className="mx-2 form-control" 
                placeholder="Enter page no" 
                onChange={(e) => setSearchPageNumber(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-success mx-2"
                onClick={handleSearchPageNavigation}
                disabled={searchPageNumber < 1}
              >
                Go
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }




  return (
    <CRow>
      <CCol xl={12}>
        <CCard>

          <CCardBody>
            <CDataTable
              items={transactionsList}
              fields={[
                { key: 'id', _classes: 'font-weight-bold' },
                { key: 'from', _classes: 'font-weight-bold' },
                { key: 'to', _classes: 'font-weight-bold' },
                { key: 'amt', _classes: 'font-weight-bold' },
              ]}
              hover
              striped
              // itemsPerPage={5}
              activePage={1}
              clickableRows
              onRowClick={(item) => {
                history.push(`/tnx-details/${item.id}/${props.selectedToken ? props.selectedToken.name : 'MetaMUI'}`);
              }
              }
              noItemsView={{ noItems: 'No Transactions Found' }}

            />
            {shouldShowPagination() && renderPaginationControls()}
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  )
}

export default Users
